import React from "react";
import {
  Badge,
  Banner,
  Layout,
  Link,
  ResourceItem,
  ResourceList,
  Select,
  Stack,
  TextField,
  TextStyle,
  Tooltip
} from "@shopify/polaris";
import { ImageMajor, TickMinor } from "@shopify/polaris-icons";
import "/assets/styles/orderItem.css";
import {
  getCategoryName,
  getCutHeight,
  getImageAttributes,
  getPatternLetter,
  getPlayersList,
  getTextAttributes,
  getVisibleAttributes,
  parseColorValue,
  parseFontName,
  parseStrokeWidth
} from "../../utils/order/customAttributes";
import { Manufacturer, MANUFACTURER_OPTIONS } from "../../utils/types";
import { isValidManufacturer } from "../../utils/order/orderItems";
import { OrderLineItem } from "../../queries/shopify/fragments/OrderLineItems";
import { CustomAttribute } from "../../models/customAttribute";

type OrderItemProps = {
  isSent: boolean;
  orderName: string;
  item: OrderLineItem;
  note: { value: string, update: (value: string) => void };
  changeItemManufacturer: (man: Manufacturer, item: OrderLineItem) => void;
};

const OrderItem = ({ orderName, item, isSent, note, changeItemManufacturer }: OrderItemProps) => {
  const productName = getCategoryName(item.customAttributes);
  const patternLetter = getPatternLetter(item.customAttributes, item.title);
  const cutHeight = getCutHeight(item.title);
  const imageAttrs = getImageAttributes(item.customAttributes);
  const visibleAttrs = getVisibleAttributes(item.customAttributes);
  const textAttrs = getTextAttributes(item.customAttributes);
  const playersList = getPlayersList(item.customAttributes);

  const changeItemManufacturerHandler = (newManufacturer: Manufacturer) => {
    if (!isSent)
      changeItemManufacturer(newManufacturer.toLowerCase() as Manufacturer, item);
  };

  return (
    <ResourceItem id={item.id} onClick={undefined}>
      {/* BLOCK WITH MESSAGE FOR OLD NAMESPACE - Task 729 */}
      {!item.product.garmentId && (
        <div style={{ marginBottom: '15px' }}>
          <div className="blocked-div" />
          <Banner title="Item relates to old namespace" status="critical">
            Please, try to use another Send To Manufacturer app for correct work
          </Banner>
        </div>
      )}

      <Layout>
        <Layout.Section secondary>
          {isSent && (
            <Badge icon={TickMinor} status="success">
              Sent
            </Badge>
          )}
          <Stack distribution="center">
            <Stack.Item>
              <div style={{ maxHeight: 300, padding: 10 }}>
                <img className="stack-item-image"
                  src={item.image ? item.image.url : ImageMajor}
                  alt={item.title}
                />
              </div>
            </Stack.Item>
          </Stack>
        </Layout.Section>

        <Layout.Section>
          <Stack distribution="equalSpacing">
            <Stack.Item>
              <h3 style={{ marginBottom: 10 }}>
                <span style={{ marginRight: 10 }}>
                  <TextStyle variation="strong">{item.title}</TextStyle>
                </span>
                <Badge status="new">{item.quantity.toString()}</Badge>
              </h3>

              <p>
                <TextStyle variation="subdued">
                  PO number: {orderName}
                </TextStyle>
              </p>
              {item.sku ? (
                <p>
                  <TextStyle variation="subdued">SKU: {item.sku}</TextStyle>
                </p>
              ) : null}
              <p>
                <TextStyle variation="subdued">{item.variantTitle}</TextStyle>
              </p>

              <p style={{ marginBottom: 10 }} />

              {productName ? (
                <p>
                  <TextStyle variation="subdued">
                    Product name: {productName}
                  </TextStyle>
                </p>
              ) : null}
              {patternLetter ? (
                <p>
                  <TextStyle variation="subdued">
                    Pattern letter: {patternLetter}
                  </TextStyle>
                </p>
              ) : null}
              {cutHeight ? (
                <p>
                  <TextStyle variation="subdued">
                    Cut height: {cutHeight}
                  </TextStyle>
                </p>
              ) : null}

              <p style={{ marginBottom: 10 }} />

              {imageAttrs.map(attr => {
                return <p key={attr.key}>
                <TextStyle variation="subdued">
                  {attr.key.substring(1)}:{' '}
                  <Link url={attr.value} external>
                    Image
                  </Link>
                </TextStyle>
              </p>
              })}
              {playersList && <TextStyle variation="subdued">Players List: <Link url={playersList.value}>Download Roster</Link></TextStyle>}
            </Stack.Item>

            <Stack.Item fill>
              <div style={{ minWidth: 300}}>
                <ResourceList
                  items={visibleAttrs}
                  renderItem={(attr) => <VisibleAttribute attr={attr} />}
                />
              </div>
            </Stack.Item>
          </Stack>

          <div style={{ marginTop: 15 }} />

          <TextField
            label="Notes"
            multiline={2}
            disabled={!note.update}
            value={note.value}
            autoComplete={'off'}
            onChange={value => {
              if (note.update) note.update(value);
            }}
          />
          {!isSent &&
            isValidManufacturer(item.product.vendor) && (
              <div className="sent-label">
                <Select
                  label="Manufacturer:"
                  labelInline
                  options={MANUFACTURER_OPTIONS.map(i => i.toUpperCase())}
                  onChange={changeItemManufacturerHandler}
                  value={item.product.vendor.toUpperCase()}
                />
              </div>
            )}
        </Layout.Section>
      </Layout>
    </ResourceItem>
  );
};

function VisibleAttribute({ attr }: { attr: CustomAttribute }) {
  const { key, value } = attr;
  const colorValue = parseColorValue(value);
  const fontName = parseFontName(value);
  const strokeWidth = parseStrokeWidth(value);
  const otherValue = !colorValue && !fontName && !strokeWidth ? value : null;

  return (
    <ResourceItem id={key} onClick={undefined}>
      <Stack spacing="extraTight">
        <Stack.Item fill><b>{key}</b></Stack.Item>
        <Stack vertical={true} alignment={'trailing'}>
          {fontName && <Stack.Item>Font: {fontName}</Stack.Item>}
          {strokeWidth && <Stack.Item>Width: {strokeWidth}</Stack.Item>}
          {colorValue && <Stack.Item>
            <div style={{ display: 'flex' }}>
              <div>{colorValue.name}</div>
              {colorValue.hex ? (
                <Tooltip
                  content={<div>{colorValue.hex}</div>}
                  dismissOnMouseOut
                  preferredPosition="above"
                >
                  <div className="color-preview" style={{ backgroundColor: colorValue.hex }} />
                </Tooltip>
              ) : null}
            </div>
          </Stack.Item>}
          {otherValue && <Stack.Item>{otherValue}</Stack.Item>}
        </Stack>
      </Stack>
    </ResourceItem>
  );
}

export default OrderItem;
